/**
 * A real .xlsx (OOXML) encoder.
 *
 * Why this exists (QA-172): the "Excel workbook" export used to be an HTML
 * <table> written out with a `.xls` extension and the Excel MIME type. Excel
 * does open it, but it opens it with a warning — "The file format and extension
 * of 'x.xls' don't match. The file could be corrupted or unsafe." — which is
 * not "opens cleanly", and everything in it lands as General text, so numbers
 * do not sum and dates do not sort. LibreOffice and Google Sheets are less
 * forgiving still.
 *
 * This writes the genuine format instead: a ZIP of the five parts Excel needs,
 * with per-cell types so a number is a number and a date is a date.
 *
 * Deliberately dependency-free. A spreadsheet library would add ~500KB to the
 * bundle to serve one button; the parts below are the whole format for a
 * single flat sheet, and the ZIP entries are STORED (uncompressed), which is
 * valid and needs no DEFLATE implementation. Typical exports are well under a
 * megabyte, and the .xlsx is still smaller than the HTML table it replaces.
 */

import { DateTime } from 'luxon'
import { getDisplayTimeZone } from '../../lib/format.js'

/* ── ZIP (STORED entries, CRC-32) ─────────────────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[i] = c >>> 0
  }
  return t
})()

function crc32(bytes) {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

const utf8 = (s) => new TextEncoder().encode(s)

/** Build a ZIP from `[{ name, data: Uint8Array }]`. */
function zip(entries) {
  const chunks = []
  const central = []
  let offset = 0

  const u16 = (n) => [n & 0xff, (n >> 8) & 0xff]
  const u32 = (n) => [n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff]

  for (const e of entries) {
    const nameBytes = utf8(e.name)
    const crc = crc32(e.data)
    const local = [
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0),            // time, date — zero is legal
      ...u32(crc), ...u32(e.data.length), ...u32(e.data.length),
      ...u16(nameBytes.length), ...u16(0),
    ]
    chunks.push(new Uint8Array(local), nameBytes, e.data)
    central.push({ name: nameBytes, crc, size: e.data.length, offset })
    offset += local.length + nameBytes.length + e.data.length
  }

  const dirStart = offset
  for (const c of central) {
    const head = [
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0),
      ...u32(c.crc), ...u32(c.size), ...u32(c.size),
      ...u16(c.name.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(0), ...u32(c.offset),
    ]
    chunks.push(new Uint8Array(head), c.name)
    offset += head.length + c.name.length
  }
  chunks.push(new Uint8Array([
    ...u32(0x06054b50), ...u16(0), ...u16(0),
    ...u16(central.length), ...u16(central.length),
    ...u32(offset - dirStart), ...u32(dirStart), ...u16(0),
  ]))

  const total = chunks.reduce((n, c) => n + c.length, 0)
  const out = new Uint8Array(total)
  let p = 0
  for (const c of chunks) { out.set(c, p); p += c.length }
  return out
}

/* ── Sheet XML ────────────────────────────────────────────────────────── */

const esc = (s) =>
  String(s ?? '')
    // XML 1.0 forbids these outright — strip rather than emit a corrupt file.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** 0 → A, 25 → Z, 26 → AA. */
export function colName(index) {
  let n = index + 1
  let out = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    out = String.fromCharCode(65 + rem) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

/** Excel's serial date: days since 1899-12-30 (its 1900 leap-year bug included). */
function excelSerial(date) {
  return (date.getTime() - Date.UTC(1899, 11, 30)) / 86400000
}

/** Excel serial for a Luxon DateTime's WALL-CLOCK. Excel cells carry no
 *  timezone, so a date-time must be written as the clock reading the person
 *  sees in the app — their display zone — not as UTC (which turned a
 *  late-evening time into the next day for anyone east of UTC, or the
 *  previous day west of it). */
function wallClockSerial(dt) {
  const wall = Date.UTC(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond)
  return (wall - Date.UTC(1899, 11, 30)) / 86400000
}

/** The person's display zone (their own, else the company's), else the
 *  browser's — the same zone every on-screen date uses. */
const viewerZone = () => getDisplayTimeZone() || 'local'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?/
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Decide a cell's type from its value. Getting this right is the whole point:
 * a number typed as text does not sum, and a date typed as text does not sort.
 */
export function cellFor(ref, value) {
  if (value === null || value === undefined || value === '') return `<c r="${ref}"/>`
  if (typeof value === 'boolean') return `<c r="${ref}" t="b"><v>${value ? 1 : 0}</v></c>`
  if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}"><v>${value}</v></c>`
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const dt = DateTime.fromJSDate(value).setZone(viewerZone())
    if (dt.isValid) return `<c r="${ref}" s="4"><v>${wallClockSerial(dt)}</v></c>`
  }

  const s = String(value).trim()
  // A bare number, but NOT something that merely looks like one and must stay
  // text: a leading zero (an ID or phone number) or a long digit run would
  // both be mangled by Excel's numeric coercion.
  if (/^-?\d+(\.\d+)?$/.test(s) && !/^0\d/.test(s) && s.replace('-', '').length <= 15) {
    return `<c r="${ref}"><v>${Number(s)}</v></c>`
  }
  if (/^-?\d+(\.\d+)?%$/.test(s)) {
    return `<c r="${ref}" s="2"><v>${Number(s.slice(0, -1)) / 100}</v></c>`
  }
  if (ISO_DATE.test(s) && !DATE_ONLY.test(s)) {
    // A date-time: an instant (with Z / an offset) is converted to the
    // person's zone; one without an offset is read as a wall-clock in it.
    const dt = DateTime.fromISO(s.replace(' ', 'T'), { zone: viewerZone() })
    if (dt.isValid) {
      const hasTime = /[T ]\d{2}:\d{2}/.test(s)
      return `<c r="${ref}" s="${hasTime ? 4 : 3}"><v>${wallClockSerial(dt)}</v></c>`
    }
  }
  if (ISO_DATE.test(s)) {
    // A calendar day ("YYYY-MM-DD") — no zone, so UTC midnight is exact.
    const d = new Date(s)
    if (!Number.isNaN(d.getTime())) {
      const hasTime = /[T ]\d{2}:\d{2}/.test(s)
      return `<c r="${ref}" s="${hasTime ? 4 : 3}"><v>${excelSerial(d)}</v></c>`
    }
  }
  // Inline strings: no sharedStrings part to keep in sync, and no count
  // attribute that can drift out of step with the rows.
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${esc(s)}</t></is></c>`
}

function sheetXml(headers, rows) {
  const lines = []
  lines.push(
    `<row r="1">` +
      headers.map((h, i) => `<c r="${colName(i)}1" s="1" t="inlineStr"><is><t>${esc(h)}</t></is></c>`).join('') +
      `</row>`,
  )
  rows.forEach((row, r) => {
    const cells = row.map((v, i) => cellFor(`${colName(i)}${r + 2}`, v)).join('')
    lines.push(`<row r="${r + 2}">${cells}</row>`)
  })
  const lastCol = colName(Math.max(headers.length - 1, 0))
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="A1:${lastCol}${rows.length + 1}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${headers.map((h, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.min(Math.max(String(h).length + 4, 12), 50)}" customWidth="1"/>`).join('')}</cols>
<sheetData>${lines.join('')}</sheetData>
<autoFilter ref="A1:${lastCol}1"/>
</worksheet>`
}

/* ── The five parts Excel needs ───────────────────────────────────────── */

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`

/** Style 1 = bold header, 2 = percent, 3 = date, 4 = date+time. */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="yyyy\\-mm\\-dd"/><numFmt numFmtId="165" formatCode="yyyy\\-mm\\-dd\\ hh:mm"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF1F5F9"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
<xf numFmtId="9" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`

const workbookXml = (sheetName) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${esc(sheetName).slice(0, 31)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`

/**
 * Build a real .xlsx.
 *
 * @param {string[]}  headers    Header row.
 * @param {unknown[][]} rows     Row values, already in column order.
 * @param {string}    [sheetName='Export']
 * @returns {Blob} an `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
 */
export function buildXlsx(headers, rows, sheetName = 'Export') {
  // Excel rejects a sheet name containing any of : \ / ? * [ ]
  const safeName = String(sheetName).replace(/[:\\/?*[\]]/g, '-') || 'Export'
  const parts = [
    { name: '[Content_Types].xml', data: utf8(CONTENT_TYPES) },
    { name: '_rels/.rels', data: utf8(ROOT_RELS) },
    { name: 'xl/workbook.xml', data: utf8(workbookXml(safeName)) },
    { name: 'xl/_rels/workbook.xml.rels', data: utf8(WORKBOOK_RELS) },
    { name: 'xl/styles.xml', data: utf8(STYLES) },
    { name: 'xl/worksheets/sheet1.xml', data: utf8(sheetXml(headers, rows)) },
  ]
  return new Blob([zip(parts)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}
